/** Evidence records (/proof) linking project examples and guides to what can actually be verified. */
import { CASES } from "@/content/cases";
import { POSTS } from "@/content/posts";
import { SITE } from "@/content/site";

export type ProofItem = {
  label: string;
  detail: string;
  /** Public URL a reader (or crawler) can open right now. */
  href?: string;
  /** How this item can be verified. */
  verification: string;
};

export type ProofEntry = {
  slug: string;
  kind: "case-study" | "article";
  /** Page this record backs. */
  subjectPath: string;
  subjectTitle: string;
  /** The entity the work was done for / about. */
  entityName: string;
  entityType: "Organization" | "Person" | "CreativeWork";
  entityDescription: string;
  /** Tamal's role on the work. */
  role: string;
  period: string;
  contributions: string[];
  methodology: string[];
  evidence: ProofItem[];
  /** Honest scope limits — what this record does NOT prove. */
  limits: string;
  lastVerified: string;
  /** /ai-answers anchors this record supports. */
  answerSlugs: string[];
};

const REVIEWED_ON = "2026-10-31";
const GUIDE_SLUGS: string[] = ["audited-100-pages-cited-by-chatgpt", "chatgpt-citation-drift-study-2026", "ai-overviews-ymyl-citation-audit", "bengali-language-aeo-benchmark-2026", "schema-validity-audit-500-saas-pages-2026", "backlinks-vs-ai-citations-conversion-study", "citation-half-life-chatgpt-forgets-brand", "state-of-aeo-bangladesh-2026-data-report", "saas-cited-47-chatgpt-answers-case-study", "zero-to-wikipedia-entity-footprint-b2b", "state-of-seo-bangladesh-2026", "bangladeshi-saas-invisible-in-chatgpt", "cite-framework-aeo", "aeo-content-refresh-cadence-re-citation", "why-89-percent-websites-fail-at-seo", "anatomy-of-chatgpt-cited-paragraph", "brand-mention-velocity-aeo-signal", "local-citation-building-bangladesh-27-directories", "common-aeo-mistakes-to-avoid", "free-llms-txt-generator-and-template", "how-llms-choose-citations-2026", "ai-first-page-template-html-schema-copy", "pagination-faceted-nav-canonical-crawl-traps", "google-ai-overviews-citation-report-2026", "schema-markup-adoption-saas-benchmark-2026", "llms-txt-adoption-top-10000-sites"];

const caseEntries: ProofEntry[] = CASES.map((c) => ({
  slug: c.slug, kind: "case-study", subjectPath: `/case-studies/${c.slug}`,
  subjectTitle: c.headline, entityName: c.client, entityType: "CreativeWork",
  entityDescription: c.summary,
  role: "Project example or illustrative guidance; outcomes not verified",
  period: c.duration,
  contributions: ["Withdrew unsupported outcome figures and campaign timelines.", "Distinguished project materials from evidence of measured results."],
  methodology: ["The suggested steps are practical guidance, not a record that those measurements were completed.", "A verified outcome requires dated property-specific records, a baseline, comparison window and attribution method."],
  evidence: (c.externalLinks ?? []).map((l) => ({ label: l.label, href: l.href,
    detail: "Public website context; not evidence of SEO outcomes or who performed the work.",
    verification: "No supporting performance export is attached. This link does not verify withdrawn results." })),
  limits: c.result,
  lastVerified: REVIEWED_ON,
  answerSlugs: [],
}));
const articleEntries: ProofEntry[] = GUIDE_SLUGS.flatMap((slug) => {
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return [];
  return [{ slug, kind: "article" as const, subjectPath: `/blog/${slug}`,
    subjectTitle: post.title, entityName: post.title, entityType: "CreativeWork" as const,
    entityDescription: post.excerpt, role: "Practical guide; no verified original dataset",
    period: `Published ${post.date}; revised ${post.updated ?? REVIEWED_ON}`,
    contributions: ["Withdrew unsupported research and outcome claims.", "Replaced them with practical guidance and stated limitations."],
    methodology: ["This is an instructional workflow, not proof that a study was conducted.", "Any future empirical claim needs dated records, transparent selection and counting rules, and reproducible analysis."],
    evidence: [{ label: "Sources in the corrected guide", href: `/blog/${slug}`,
      detail: "Official references support implementation guidance, not the withdrawn original findings.",
      verification: "Follow the source links in the guide. No original dataset or client-outcome archive is attached." }],
    limits: "The earlier research or outcome claims have no supporting records on file and are withdrawn. This guide establishes no ranking, adoption rate, citation multiplier or guaranteed result.",
    lastVerified: REVIEWED_ON, answerSlugs: [],
  }];
});
export const PROOF_ENTRIES: ProofEntry[] = [...caseEntries, ...articleEntries];
export function findProof(slug: string) { return PROOF_ENTRIES.find((p) => p.slug === slug); }
export function proofForSubject(subjectPath: string) { return PROOF_ENTRIES.find((p) => p.subjectPath === subjectPath); }
export const PROOF_AUTHOR = {
  "@type": "Person",
  "@id": `${SITE.url}/#person`,
  name: SITE.fullName,
  alternateName: SITE.name,
  url: `${SITE.url}/about`,
  jobTitle: "SEO, AEO & GEO Consultant",
  email: `mailto:${SITE.email}`,
  telephone: SITE.phone,
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.streetAddress,
    addressLocality: SITE.city,
    addressRegion: SITE.region,
    postalCode: SITE.postalCode,
    addressCountry: SITE.countryCode,
  },
  sameAs: Object.values(SITE.social),
};
